import {
  useState,
  FC,
  ReactElement,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { FlexRow } from "../../../flexRow/FlexRow";

export interface Option {
  key: string;
  value: string | boolean;
  renderOption: ReactElement;
}

interface CustomDropdownOptionProps {
  onClick: () => void;
  children: ReactElement;
}

interface CustomDropdownProps {
  options: Option[];
  isSmall?: boolean;
  className?: string;
  selectedOption?: string | boolean;
  setSelectedOption: (value: string | boolean) => void;
}

const CustomDropdownOption: FC<CustomDropdownOptionProps> = ({
  onClick,
  children,
}) => (
  <div
    onClick={onClick}
    className="flex items-center cursor-pointer p-2 hover:bg-gray-200"
  >
    {children}
  </div>
);

export interface CustomDropdownHandles {
  open: () => void;
  close: () => void;
  scrollIntoView: () => void;
}

export const CustomDropdown = forwardRef<
  CustomDropdownHandles,
  CustomDropdownProps
>(
  (
    { options, selectedOption, setSelectedOption, isSmall, className = "" },
    ref
  ) => {
    const domRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      scrollIntoView: () =>
        domRef.current?.scrollIntoView({ behavior: "smooth" }),
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
    }));

    return (
      <div ref={domRef} className="relative w-full min-w-[85px] z-10">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full ${
            isSmall ? "p-2" : "p-3 px-[18px]"
          } border rounded-[10px] ${className}`}
        >
          <FlexRow className="w-full justify-between items-center">
            {options.find((x) => x.value === selectedOption)?.renderOption}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1em"
              height="1em"
              viewBox="0 0 1024 1024"
            >
              <path
                fill="currentColor"
                d="M104.704 338.752a64 64 0 0 1 90.496 0l316.8 316.8l316.8-316.8a64 64 0 0 1 90.496 90.496L557.248 791.296a64 64 0 0 1-90.496 0L104.704 429.248a64 64 0 0 1 0-90.496"
              />
            </svg>
          </FlexRow>
        </button>
        {isOpen && (
          <div className="absolute top-full left-0 w-full border bg-white rounded-lg">
            {options.map((option) => (
              <CustomDropdownOption
                key={option.key}
                onClick={() => {
                  setSelectedOption(option.value);
                  setIsOpen(false);
                }}
              >
                {option.renderOption}
              </CustomDropdownOption>
            ))}
          </div>
        )}
      </div>
    );
  }
);

CustomDropdown.displayName = "CustomDropdown";
